import { useId, useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { checkAmount, formatUsd } from "../household/moneyInput";
import { schoolPlan, loanOutcome, yearsPhrase, yearsWord, FEDERAL_UNDERGRAD_RATE } from "./collegeCostMath";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { FirstPaymentDateField, useOptionalStartDate } from "../converters/FirstPaymentDateField";
import { formatFullDate, planYearLabel, dateAfterMonths } from "../converters/paymentDates";

const TOOL_INFO_CONTENT = {
  what: `Estimates total college cost of attendance across multiple years — tuition, room and board, books, and fees — with an annual increase rate and any scholarships netted out. Add family money and work-study to see how much you would borrow and the monthly payment, and add up to three more schools to compare side by side.`,
  how: `Enter your annual costs by category, the number of years, an expected annual increase rate, and any scholarships or aid, and your total net cost over the full program is calculated. Add the date the first year starts to see the months each year covers; it only labels the years and never changes a cost. The loan figures are estimates: interest is counted from each year's loan until graduation, then added to the balance when repayment starts.`,
  tip: `Check your specific target school's published cost of attendance rather than relying on national averages — tuition and fees vary enormously between institutions, even within the same state.`,
};

type Extra = { id: number; name: string; cost: string; aid: string };
const MAX_EXTRA = 3;

function Field({ label, value, onChange, error, hint, inputMode }: { label: string; value: string; onChange: (v: string) => void; error?: string; hint?: string; inputMode?: "numeric" | "decimal" | "text" }) {
  const id = useId();
  return (
    <div>
      <Label htmlFor={id} className="mb-1 block text-sm">{label}</Label>
      <Input id={id} value={value} inputMode={inputMode ?? "decimal"} maxLength={inputMode === "text" ? 40 : undefined} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-err` : undefined} className={error ? "border-destructive" : ""} />
      {error ? <p id={`${id}-err`} role="alert" className="text-xs text-destructive mt-1">{error}</p> : hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
    </div>
  );
}

export default function CollegeCostCalculator() {
  const [tuition, setTuition] = useState("11000");
  const [roomBoard, setRoomBoard] = useState("12000");
  const [books, setBooks] = useState("1200");
  const [otherFees, setOtherFees] = useState("1500");
  const [misc, setMisc] = useState("2000");
  const [years, setYears] = useState("4");
  const [inflationRate, setInflationRate] = useState("4");
  const [scholarships, setScholarships] = useState("0");
  const [schoolName, setSchoolName] = useState("School 1");
  const [extras, setExtras] = useState<Extra[]>([]);
  const [family, setFamily] = useState("0");
  const [workStudy, setWorkStudy] = useState("0");
  const [loanRate, setLoanRate] = useState(String(FEDERAL_UNDERGRAD_RATE.pct));
  const [loanTerm, setLoanTerm] = useState("10");
  const startState = useOptionalStartDate();
  const start = startState.start;

  const c = {
    tuition: checkAmount(tuition, { label: "Tuition & fees", blank: 0 }),
    roomBoard: checkAmount(roomBoard, { label: "Room & board", blank: 0 }),
    books: checkAmount(books, { label: "Books & supplies", blank: 0 }),
    otherFees: checkAmount(otherFees, { label: "Other fees", blank: 0 }),
    misc: checkAmount(misc, { label: "Personal / misc", blank: 0 }),
    years: checkAmount(years, { label: "Years", integer: true, min: 1, max: 12 }),
    inflation: checkAmount(inflationRate, { label: "Annual increase", blank: 0, negative: true, min: -50, max: 50 }),
    aid: checkAmount(scholarships, { label: "Scholarships/aid", blank: 0 }),
    family: checkAmount(family, { label: "Family contribution", blank: 0 }),
    workStudy: checkAmount(workStudy, { label: "Work-study", blank: 0 }),
    loanRate: checkAmount(loanRate, { label: "Loan rate", blank: 0, max: 100 }),
    loanTerm: checkAmount(loanTerm, { label: "Repayment term", integer: true, min: 1, max: 30 }),
  };
  const extraChecks = extras.map((x) => ({ cost: checkAmount(x.cost, { label: "Year 1 cost", blank: 0 }), aid: checkAmount(x.aid, { label: "Aid per year", blank: 0 }) }));
  const mainValid = Object.values(c).every((v) => v.value !== null);
  const valid = mainValid && extraChecks.every((e) => e.cost.value !== null && e.aid.value !== null);

  const result = useMemo(() => {
    if (!valid) return null;
    const baseAnnual = c.tuition.value! + c.roomBoard.value! + c.books.value! + c.otherFees.value! + c.misc.value!;
    const funding = { familyPerYear: c.family.value!, workStudyPerYear: c.workStudy.value!, ratePct: c.loanRate.value!, termYears: c.loanTerm.value! };
    const make = (name: string, base: number, aid: number) => {
      const plan = schoolPlan({ baseAnnual: base, years: c.years.value!, increasePct: c.inflation.value!, aidPerYear: aid });
      return { name, plan, loan: loanOutcome(plan.rows.map((r) => r.net), funding) };
    };
    const schools = [
      make(schoolName.trim() || "School 1", baseAnnual, c.aid.value!),
      ...extras.map((x, i) => make(x.name.trim() || `School ${i + 2}`, extraChecks[i].cost.value!, extraChecks[i].aid.value!)),
    ];
    return { baseAnnual, schools, funding };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, tuition, roomBoard, books, otherFees, misc, years, inflationRate, scholarships, schoolName, extras, family, workStudy, loanRate, loanTerm]);

  const { blocked } = useLogToolUsage(!!result, 3000);

  const setExtra = (id: number, patch: Partial<Extra>) => setExtras((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const addSchool = () => setExtras((xs) => (xs.length >= MAX_EXTRA ? xs : [...xs, { id: Date.now() + xs.length, name: `School ${xs.length + 2}`, cost: "", aid: "0" }]));

  const first = result?.schools[0];
  const nYears = c.years.value ?? 0;
  const money = (n: number) => formatUsd(n);

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          {extras.length > 0 && (
            <Field label="School name" value={schoolName} onChange={setSchoolName} inputMode="text" />
          )}
          <Field label="Tuition & Fees (Year 1, $)" value={tuition} onChange={setTuition} error={c.tuition.error} />
          <Field label="Room & Board ($)" value={roomBoard} onChange={setRoomBoard} error={c.roomBoard.error} />
          <Field label="Books & Supplies ($)" value={books} onChange={setBooks} error={c.books.error} />
          <Field label="Other Fees ($)" value={otherFees} onChange={setOtherFees} error={c.otherFees.error} />
          <Field label="Personal / Misc ($)" value={misc} onChange={setMisc} error={c.misc.error} />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Years" value={years} onChange={setYears} error={c.years.error} inputMode="numeric" />
            <Field label="Annual Increase (%)" value={inflationRate} onChange={setInflationRate} error={c.inflation.error} />
            <Field label="Scholarships/Aid per Year ($)" value={scholarships} onChange={setScholarships} error={c.aid.error} />
          </div>
          <FirstPaymentDateField id="cc-start" label="First year starts (optional)" state={startState} help="Leave empty for Year 1, Year 2 and so on. With a date, each year shows the months it covers. The date only labels the years; the costs do not change." />

          <fieldset className="space-y-3 rounded-lg border p-3">
            <legend className="px-1 text-sm font-medium">Paying for it (optional)</legend>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Family Contribution per Year ($)" value={family} onChange={setFamily} error={c.family.error} />
              <Field label="Work-Study per Year ($)" value={workStudy} onChange={setWorkStudy} error={c.workStudy.error} />
              <Field label="Loan Rate (% APR)" value={loanRate} onChange={setLoanRate} error={c.loanRate.error} hint={`Federal undergraduate rate for ${FEDERAL_UNDERGRAD_RATE.awardYear}. Use your own rate if different.`} />
              <Field label="Repayment Term (years)" value={loanTerm} onChange={setLoanTerm} error={c.loanTerm.error} inputMode="numeric" />
            </div>
          </fieldset>

          {extras.map((x, i) => (
            <fieldset key={x.id} className="space-y-3 rounded-lg border p-3">
              <legend className="px-1 text-sm font-medium">Compare: school {i + 2}</legend>
              <Field label="School name" value={x.name} onChange={(v) => setExtra(x.id, { name: v })} inputMode="text" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Year 1 Total Cost ($)" value={x.cost} onChange={(v) => setExtra(x.id, { cost: v })} error={extraChecks[i].cost.error} hint="Tuition, housing, books, fees and the rest." />
                <Field label="Aid per Year ($)" value={x.aid} onChange={(v) => setExtra(x.id, { aid: v })} error={extraChecks[i].aid.error} />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setExtras((xs) => xs.filter((y) => y.id !== x.id))}>Remove school {i + 2}</Button>
            </fieldset>
          ))}
          {extras.length < MAX_EXTRA && (
            <Button type="button" variant="outline" onClick={addSchool}>Compare another school</Button>
          )}
        </div>

        <div data-result>
          {c.years.value === null ? (
            <p role="status" className="text-sm text-center text-muted-foreground py-6">Fix the number of years to see the total.</p>
          ) : !result ? (
            <p role="status" className="text-sm text-center text-muted-foreground py-6">Fix the red boxes to see your results.</p>
          ) : blocked ? (
            <p className="text-sm text-center text-muted-foreground py-6">— blocked at today's usage limit —</p>
          ) : (
            <>
              <Card className="p-6 text-center mb-4">
                <p className="text-3xl font-bold text-primary">{money(first!.plan.total)}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Total cost, net of aid, over {yearsWord(nYears)}{extras.length > 0 ? ` (${first!.name})` : ""}
                </p>
                {start && first!.plan.rows.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="cc-dates">
                    From {formatFullDate(start)} to {formatFullDate(dateAfterMonths(start, first!.plan.rows.length * 12))}.
                  </p>
                )}
              </Card>
              {first!.plan.aidExceedsYears.length > 0 && (
                <p role="status" data-testid="cc-aid-note" className="text-xs mb-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2">
                  Your aid is more than the cost in {yearsPhrase(first!.plan.aidExceedsYears)}. Those years show $0: aid left over is not counted, and it does not carry into later years.
                </p>
              )}
              <div className="space-y-2">
                {first!.plan.rows.map(y => (
                  <div key={y.year} className="flex items-center justify-between px-4 py-2 rounded-lg border bg-card text-sm">
                    <span>Year {y.year}{start ? ` (${planYearLabel(start, y.year)})` : ""}</span>
                    <span className="font-medium">{money(y.net)}</span>
                  </div>
                ))}
              </div>

              <Card className="p-4 mt-4" data-testid="cc-loan">
                <p className="text-sm font-medium mb-2">{extras.length > 0 ? `Borrowing for ${first!.name}` : "How much you would borrow"}</p>
                {first!.loan.borrowed === 0 ? (
                  <p className="text-sm text-muted-foreground">With the family contribution, work-study and aid you entered, nothing is left to borrow.</p>
                ) : (
                  <dl className="text-sm space-y-1">
                    <div className="flex justify-between"><dt>Gap to cover with loans</dt><dd className="font-medium">{money(first!.loan.borrowed)}</dd></div>
                    <div className="flex justify-between"><dt>Interest while in school</dt><dd className="font-medium">{money(first!.loan.schoolInterest)}</dd></div>
                    <div className="flex justify-between"><dt>Debt at graduation</dt><dd className="font-medium">{money(first!.loan.debtAtGraduation)}</dd></div>
                    <div className="flex justify-between"><dt>Monthly payment ({yearsWord(result.funding.termYears)} at {result.funding.ratePct}%)</dt><dd className="font-medium">{formatUsd(first!.loan.monthlyPayment, 2)}</dd></div>
                    <div className="flex justify-between"><dt>Interest during repayment</dt><dd className="font-medium">{money(first!.loan.repaymentInterest)}</dd></div>
                  </dl>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Estimate: each year's gap is borrowed at the start of the year, and interest builds up until graduation, as on unsubsidized federal loans. Grace period not counted. For extra payments and a dated schedule, use the{" "}
                  <a className="underline" href="/tool/student-loan-calculator">Student Loan Repayment Calculator</a>.
                </p>
              </Card>

              {result.schools.length > 1 && (
                <div className="mt-4 overflow-x-auto" data-testid="cc-compare">
                  <table className="w-full text-sm border rounded-lg">
                    <caption className="text-left text-sm font-medium mb-1">Schools side by side ({yearsWord(nYears)}, {c.inflation.value}% yearly increase)</caption>
                    <thead>
                      <tr className="bg-muted/40 text-left">
                        <th scope="col" className="p-2">School</th>
                        <th scope="col" className="p-2 text-right">Net total</th>
                        <th scope="col" className="p-2 text-right">To borrow</th>
                        <th scope="col" className="p-2 text-right">Debt at graduation</th>
                        <th scope="col" className="p-2 text-right">Per month</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.schools.map((s, i) => (
                        <tr key={i} className="border-t">
                          <th scope="row" className="p-2 text-left font-medium">{s.name}</th>
                          <td className="p-2 text-right">{money(s.plan.total)}</td>
                          <td className="p-2 text-right">{money(s.loan.borrowed)}</td>
                          <td className="p-2 text-right">{money(s.loan.debtAtGraduation)}</td>
                          <td className="p-2 text-right">{formatUsd(s.loan.monthlyPayment, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {result.schools.some((s) => s.plan.aidExceedsYears.length > 0) && (
                    <p className="text-xs text-muted-foreground mt-1">Where aid is more than a year's cost, that year counts as $0.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-4">
        Aid is held flat every year while costs rise with the annual increase, and grants and loans are not separated: enter only aid you do not repay. This estimates cost of attendance based on the numbers you enter — check
        your specific school's published cost of attendance for exact figures,
        since tuition and fees vary widely. For your net price after aid at a specific school, use the{" "}
        <a className="underline" href="https://collegecost.ed.gov/net-price" target="_blank" rel="noopener noreferrer">U.S. Department of Education net price calculator (opens in a new tab)</a>.
      </p>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
